import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/api';
import BrandPanel from '../components/BrandPanel';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Button, Input, Field } from '../components/ui';
import { getApiErrorMessage } from '../utils/apiErrors';

function ResetPassword() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('Passwords do not match'));
      return;
    }

    setLoading(true);
    try {
      await authService.confirmPasswordReset(uid, token, password);
      navigate('/login', { state: { message: t('Password reset successfully. Please log in.') } });
    } catch (err) {
      setError(getApiErrorMessage(err, t, 'This reset link is invalid or has expired.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-surface-50">
      <BrandPanel />

      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        <div
          className="absolute right-4 sm:right-6"
          style={{ top: 'max(1rem, calc(env(safe-area-inset-top) + 0.5rem))' }}
        >
          <LanguageSwitcher />
        </div>

        <div className="max-w-sm w-full">
          <Link to="/" className="mb-8 lg:hidden inline-block">
            <Logo size={36} textColor="text-gray-900" />
          </Link>

          <h2 className="text-2xl font-bold font-display tracking-tight text-gray-900">{t('Choose a new password')}</h2>
          <p className="text-sm text-gray-500 mt-1 mb-8">{t('Make it something you have not used before.')}</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label={t('New Password')} required>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                minLength={8}
              />
            </Field>

            <Field label={t('Confirm New Password')} required>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
              />
            </Field>

            <Button type="submit" loading={loading} className="w-full" size="lg">
              {t('Reset Password')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
